import React from "react";
import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";

interface Props {
  show: boolean;
  handleClose: () => void;
  policyData: {
    returnDays: number;
    exchangeDays: number;
    buybackAfter: number;
  };
  onSave: (data: {
    returnDays: number;
    exchangeDays: number;
    buybackAfter: number;
  }) => void;
}

const EditReturnPolicyModal: React.FC<Props> = ({
  show,
  handleClose,
  policyData,
  onSave,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      returnDays: policyData?.returnDays?.toString(),
      exchangeDays: policyData?.exchangeDays?.toString(),
      buybackAfter: policyData?.buybackAfter?.toString(),
    },
  });

  React.useEffect(() => {
    if (show) {
      reset({
        returnDays: policyData?.returnDays?.toString(),
        exchangeDays: policyData?.exchangeDays?.toString(),
        buybackAfter: policyData?.buybackAfter?.toString(),
      });
    }
  }, [show, policyData, reset]);

  const onSubmit = async (data: {
    returnDays: string;
    exchangeDays: string;
    buybackAfter: string;
  }) => {
    await onSave({
      returnDays: parseInt(data.returnDays),
      exchangeDays: parseInt(data.exchangeDays),
      buybackAfter: parseInt(data.buybackAfter),
    });
  };

  const validateNumber = (value: string) => {
    if (!value) return "Required";
    if (!/^\d+$/.test(value)) return "Only numbers allowed";
    if (parseInt(value) > 365) return "Cannot exceed 365 days";
    return true;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="fade"
      dialogClassName="modal-dialog-centered"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-black">Update Policy Setting</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-4 mb-3">
              <Form.Label>Return Within (days)</Form.Label>
              <Form.Control
                type="text"
                {...register("returnDays", {
                  validate: validateNumber,
                })}
                maxLength={3}
                isInvalid={!!errors.returnDays}
                className="form-control form-control-sm"
              />
              <Form.Control.Feedback type="invalid">
                {errors.returnDays?.message}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-4 mb-3">
              <Form.Label>Exchange After (days)</Form.Label>
              <Form.Control
                type="text"
                {...register("exchangeDays", {
                  validate: validateNumber,
                })}
                maxLength={3}
                isInvalid={!!errors.exchangeDays}
                className="form-control form-control-sm"
              />
              <Form.Control.Feedback type="invalid">
                {errors.exchangeDays?.message}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-4 mb-3">
              <Form.Label>Buyback After (days)</Form.Label>
              <Form.Control
                type="text"
                {...register("buybackAfter", {
                  validate: validateNumber,
                })}
                maxLength={3}
                isInvalid={!!errors.buybackAfter}
                className="form-control form-control-sm"
              />
              <Form.Control.Feedback type="invalid">
                {errors.buybackAfter?.message}
              </Form.Control.Feedback>
            </div>
          </div>

          <div className="d-flex justify-content-end align-items-end mt-4">
            <button
              type="button"
              className="btn add-btn me-1"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn submit-btn"
            >
              {isSubmitting ? "Updating..." : "Update"}
            </button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditReturnPolicyModal;